<script lang="ts">
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faEdit, faFileImport, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { SchedulesRepository } from '../../../modules/schedulerApi/schedules/schedules.repository'
    import { SchedulesService } from '../../../modules/schedulerApi/schedules/schedules.service'
    import { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import type { CreateScheduleDto } from '../../../modules/schedulerApi/schedules/dto/create-schedule.dto'
    import type { UpdateScheduleDto } from '../../../modules/schedulerApi/schedules/dto/update-schedule.dto'
    import dayjs from 'dayjs'
    import ScheduleConfigSchedulersRange from './schedule-config-schedulers-range.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine, GridToggle } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZinputSwitch from '../../_common/z/zinput-switch.svelte'
    import type { Profile } from '../../../modules/toolsApi/sites/profiles/profile'
    import type { User } from '../../../modules/usersApi/users/user'
    import AudienceFileImport from '../../automacao/audience/audience-file-import.svelte'
    import type { ImportScheduleDto } from '../../../modules/schedulerApi/schedules/dto/import-schedule.dto'
    import Popover from '../../_common/popover.svelte'
    // ##############################################################################################################

    const entityId = 'scheduleId'

    let usersAll: boolean = true
    let attendantsAll: boolean = true

    let users: User[] = []

    async function loadUsers() {
        users = await window.api.users.companies.getCompanyUsers(600)
    }

    $: userOptions = users.map((user) => ({ value: user.userId, label: user.userName }))

    let appSlug: string = ''
    const rep = new SchedulesRepository()
    const service = new SchedulesService(rep)
    let entity: Schedule = new Schedule()

    const widthFactorOptions = [
        { value: 1, label: '1x' },
        { value: 2, label: '2x' },
    ]

    async function editEntity(id: string) {
        try {
            show = true
            await loadUsers()
            entity = new Schedule()
            entity = await service.getOne(id)
            usersAll = entity.users.length === 0
            attendantsAll = entity.attendants.length === 0
            if (!entity.widthFactor) entity.widthFactor = 1
            let start = new Date()
            start.setHours(0, entity.startMinute, 0)
            startTime = new Date(start)

            let end = new Date()
            end.setHours(0, entity.endMinute, 0)
            endTime = new Date(end)
            step = 2
            console.log('entity', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        await loadUsers()
        entity = new Schedule()
        entity.users = []
        entity.attendants = []
        usersAll = true
        attendantsAll = true
        entity.range = 15
        entity.onlineFitsAllowed = 0
        entity.widthFactor = 1
        entity.position = entities.length + 1
        step = 2
    }

    function validateDto(dto: Partial<Schedule>) {
        if (dto.public && !dto.onlineAtendant) {
            window.error('Informe quem será o atendente quando um agendamento for criado online.')
            return false
        }
        if (!Number.isInteger(dto.onlineFitsAllowed) || dto.onlineFitsAllowed < 0) {
            window.error('Informe um número inteiro de encaixes permitidos (0 para não permitir encaixes).')
            return false
        }
        return true
    }

    async function createEntity() {
        try {
            show = true
            if (usersAll) entity.users = []
            if (attendantsAll) entity.attendants = []
            const dto: CreateScheduleDto = {
                endMinute: entity.endMinute,
                label: entity.label,
                position: entity.position,
                range: entity.range,
                startMinute: entity.startMinute,
                slots: entity.slots,
                public: entity.public,
                onlineFitsAllowed: Number(entity.onlineFitsAllowed || 0),
                picture: entity.picture,
                users: entity.users,
                attendants: entity.attendants,
                onlineAtendant: entity.onlineAtendant,
                scheduleGroupId: entity.group?.scheduleGroupId,
                widthFactor: Number(entity.widthFactor) === 2 ? 2 : 1,
            }
            if (!validateDto(dto)) return
            entity = await service.create(dto)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            if (usersAll) entity.users = []
            if (attendantsAll) entity.attendants = []
            const dto: UpdateScheduleDto = {
                endMinute: entity.endMinute,
                label: entity.label,
                position: entity.position,
                range: entity.range,
                startMinute: entity.startMinute,
                slots: entity.slots,
                public: entity.public,
                onlineFitsAllowed: Number(entity.onlineFitsAllowed || 0),
                picture: entity.picture,
                users: entity.users,
                attendants: entity.attendants,
                onlineAtendant: entity.onlineAtendant,
                scheduleGroupId: entity.group?.scheduleGroupId,
                widthFactor: Number(entity.widthFactor) === 2 ? 2 : 1,
            }
            if (!validateDto(dto)) return
            await service.update(entity[entityId], dto)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (!validateFields()) return
        if (entity[entityId]) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    function validateFields() {
        if (!entity.label) {
            evaluateError('Informe o nome da agenda')
            return false
        }
        if (!startTime || !endTime) {
            evaluateError('Informe o horário de início e fim das atividades')
            return false
        }
        if (entity.range < 10 || entity.range > 1440) {
            evaluateError([
                'Informe o intervalo entre um agendamento e outro.',
                'O intervalo mínimo é de 10 minutos',
                'O intervalo máximo é de 1440 minutos (24 horas)',
            ])
            return false
        }
        entity.startMinute = startTime?.getHours() * 60 + startTime?.getMinutes()
        entity.endMinute = endTime?.getHours() * 60 + endTime?.getMinutes()
        if (entity.endMinute - entity.startMinute < 0) {
            let mens = 'O horário de início deve ser anterior ao horário de término.'
            if (entity.endMinute === 0) mens += '\nCaso o horário de término seja meia-noite informe 23:59.'
            evaluateError(mens)
            return false
        }
        return true
    }

    let checkTime = null
    function onStartChange() {
        console.log('changed...', startTime)
        entity.startMinute = startTime?.getHours() * 60 + startTime?.getMinutes()
        entity.endMinute = endTime?.getHours() * 60 + endTime?.getMinutes()
        entity = entity
        if (checkTime) clearTimeout(checkTime)
        checkTime = setTimeout(() => {
            if (entity.endMinute - entity.startMinute < 0) {
                let mens = 'O horário de início deve ser anterior ao horário de término.'
                if (entity.endMinute === 0) mens += '\nCaso o horário de término seja meia-noite informe 23:59.'
                evaluateError(mens)
            }
        }, 3000)
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await service.getAll({ all: true })
            // check if online schedulings is active
            await checkAppSlug()
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function checkAppSlug() {
        try {
            const profile: Profile = await window.api.tools.sites.profiles.getOne(window.store.company.companyId)
            appSlug = profile?.slug
        } catch (error) {}
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        let siteUrl = ''
        if (location.href.includes('127.0.0') || location.href.includes('localhost')) {
            siteUrl = 'http://localhost:5173/' + appSlug + '/scheduler'
        } else {
            siteUrl = 'https://agenda.zandu.com.br/' + appSlug + '/scheduler'
        }
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Agendas'
        if (appSlug) {
            grid.subtitle = 'URL para agendamentos online: ' + siteUrl
        } else {
            grid.subtitle = ''
        }
        grid.cols.push(new GridCol({ content: 'Ativo', type: GridColTypeEnum.toggle }))
        grid.cols.push(new GridCol({ content: 'Nome da agenda' }))
        grid.cols.push(new GridCol({ content: 'ID da agenda' }))
        grid.cols.push(new GridCol({ content: 'URL da agenda' }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const toggle = new GridToggle({
                callback: () => {
                    toggleStatus(item.scheduleId, item.status)
                },
                checked: item.status === 100,
            })
            const btnPerson = new GridButton({
                callback: () => editEntity(item.scheduleId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: toggle })
            line.slots.push({ content: item.label })
            line.slots.push({ content: item.scheduleId })
            if (appSlug) {
                if (item.public) {
                    line.slots.push({ content: siteUrl + '/?s=' + item.scheduleId })
                } else {
                    line.slots.push({ content: '[agenda com agendamento online desativado]' })
                }
            } else {
                line.slots.push({ content: '[agendamento online não configurado]' })
            }

            line.slots.push({ content: [btnPerson] })
            grid.lines.push(line)
        }
    }

    async function toggleStatus(id: string, currentStatus: number) {
        try {
            let status = currentStatus === 100 ? 70 : 100
            await window.api.schedulers.schedules.changeStatus(id, status)
        } catch (error) {
            window.error(error)
        } finally {
            await loadEntities()
        }
    }

    let allowRemove = false
    async function removeEntity() {
        allowRemove = false
        step = 3
    }

    async function removeEntityDo() {
        try {
            show = true
            await service.remove(entity[entityId])
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: Schedule[] = []
    let startTime: Date = null
    let endTime: Date = null

    $: positionOptions = Array.from({ length: entities.length + 1 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))

    // no type="file" o Zinput usa "options" como padrões do accept nativo, não como escolhas
    const imageAccept = [{ value: 'image/*', label: 'Imagens' }]

    onMount(async () => {
        await loadEntities()
    })

    function importFile() {
        step = 4
    }

    function improveCSVFile(data: any[]) {
        const badDate1 = ['Qui  ', 'Sex  ', 'Sáb  ', 'Dom  ', 'Seg  ', 'Ter  ', 'Qua  ']
        for (let item of data) {
            if (item['appointmentStart']?.includes(' às')) {
                item['appointmentStart'] = item['appointmentStart'].split(' ')[0]
            }
            if (item['appointmentDate']?.includes(' às')) {
                item['appointmentDate'] = item['appointmentDate'].split(' ')[0]
            }
            if (badDate1.includes(item['appointmentStart']?.substring(0, 5))) {
                item['appointmentStart'] = item['appointmentStart'].split('  ')[1]
            }
            if (badDate1.includes(item['appointmentDate']?.substring(0, 5))) {
                item['appointmentDate'] = item['appointmentDate'].split('  ')[1]
            }
        }
        console.log('csvData_optim', data)
        return data
    }

    let sampleImport = false
    async function proccessImport(sample: boolean) {
        if (!validImport) {
            window.error('Dados inválidos. Informe todos os campos.')
            return
        }
        if (csvData.length < 2) {
            window.error('Arquivo com poucos dados. Utilize a importação para grandes quantidades de dados.')
            return
        }
        if (!csvData[1]['appointmentStart'] || !csvData[1]['scheduleLabel'] || !csvData[1]['personName']) {
            window.error('Todos os campos obrigatórios devem ser informados e devem possuir dados.')
            return
        }
        if (!csvData[1]['appointmentServices']) {
            if (
                !confirm('Atenção, importante:\nNão foi informada uma coluna com o nome do serviço realizado.\nDeseja mesmo importar sem um serviço associado?')
            ) {
                return
            }
        }

        if (!csvData[1]['personPhone']) {
            if (
                !confirm(
                    'Atenção: você não selecionou uma coluna para o "Celular do cliente".\nVocê pode continuar, mas pessoas com o mesmo nome terão seus agendamentos unificados.\nDeseja mesmo continuar?',
                )
            ) {
                return
            }
        }

        //
        sampleImport = sample
        let data = improveCSVFile(csvData)
        if (sample) data = data.slice(0, 10)
        //
        availableSchedules = [...new Set(data.map((item) => item['scheduleLabel']))]
        selectedSchedules = availableSchedules
    }

    async function executeImport() {
        let data = sampleImport ? csvData.slice(0, 10) : csvData
        let scheduleCount = [...new Set(data.map((item) => item['scheduleLabel']))].length
        if (selectedSchedules.length > 15) {
            window.error('Você pode importar no máximo 15 agendas.')
            return
        }
        console.log('loaded data', data)
        availableSchedules = []
        show = true
        let success = 0
        let dto: ImportScheduleDto[] = []
        for (const el of data) {
            if (!el['personName']) continue
            if (!el['scheduleLabel']) continue
            if (!selectedSchedules.includes(el['scheduleLabel'])) continue
            const item: ImportScheduleDto = {
                appointmentDate: el['appointmentDate'],
                appointmentEnd: el['appointmentEnd'],
                appointmentServices: el['appointmentServices'] || '[ serviço não informado ]',
                appointmentStart: el['appointmentStart'],
                appointmentStatus: el['appointmentStatus'],
                personName: el['personName'],
                scheduleLabel: el['scheduleLabel'],
                personPhone: el['personPhone'],
            }
            dto.push(item)
            success++
        }
        let result = null
        try {
            result = await window.api.schedulers.schedules.importSchedules(dto)
            await loadEntities()
            window.alert('Importação enviada.\nAguarde alguns minutos até que a importação seja processada.')
        } catch (error) {
            window.error(error)
        }
        selectedSchedules = []
        show = false
    }

    function abortImport() {
        availableSchedules = []
    }

    let csvData = []
    let csvTypes = []
    let availableSchedules: string[] = []
    let selectedSchedules: string[] = []
    csvTypes.push({ value: 'scheduleLabel', label: 'Nome da agenda*', removeOnEmpty: false })
    csvTypes.push({ value: 'personName', label: 'Nome do cliente*', removeOnEmpty: true })
    csvTypes.push({ value: 'personPhone', label: 'Celular do cliente', removeOnEmpty: false })
    csvTypes.push({ value: 'appointmentDate', label: 'Data do agendamento', removeOnEmpty: false })
    csvTypes.push({ value: 'appointmentStart', label: 'Início do atendimento*', removeOnEmpty: false })
    csvTypes.push({ value: 'appointmentEnd', label: 'Fim do atendimento', removeOnEmpty: false })
    csvTypes.push({ value: 'appointmentServices', label: 'Serviço realizado*', removeOnEmpty: false })
    csvTypes.push({ value: 'appointmentStatus', label: 'Status do agendamento', removeOnEmpty: false })
    csvTypes.push({ value: '-', label: 'Outro / Não importar', removeOnEmpty: false })
    let patherns: { pathern: string; fields: string[] }[] = []
    patherns.push({
        pathern: 'Profissional,Data,Horário Inicio,Horário Termino,Horário,Cliente,Serviço(s),Preço,Situação,Observação,Cobrado por Comanda?',
        fields: [
            'scheduleLabel',
            'appointmentDate',
            'appointmentStart',
            'appointmentEnd',
            '-',
            'personName',
            'appointmentServices',
            '-',
            'appointmentStatus',
            '-',
            '-',
        ],
    })
    patherns.push({
        pathern: 'Data;Nome;Contato;Horário;Agendado por;Status;;;Plano',
        fields: ['appointmentDate', 'personName', 'personPhone', 'appointmentStart', 'scheduleLabel', 'appointmentStatus', '-', '-'],
    })
    patherns.push({
        pathern:
            'AddInfo;AlertInfo;AppointmentId;AtomicDate;CancelBy;CancelReason;Canceled;CanceledDate;CanceledUserId;CategoryColor;CategoryDescription;CategoryId;CheckinTime;',
        fields: [
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            'scheduleLabel',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            'personPhone',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            'personName',
            '-',
            'appointmentServices',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            'appointmentStatus',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            'appointmentDate',
            '-',
            'appointmentStart',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
            '-',
        ],
    })
    let validImport = false
</script>

<LoadSpinner {show} />

{#if availableSchedules.length > 0}
    <Popover on:close={abortImport} width="690px" height="100px">
        <div class="config-box m-4">
            <p class="subtitle">Quais agendas deseja importar?</p>

            <select name="scheduleSel" id="scheduleSel" multiple size="10" bind:value={selectedSchedules} class="form-control">
                {#each availableSchedules as schedule}
                    {#if availableSchedules.includes(schedule)}
                        <option selected value={schedule}>{schedule}</option>
                    {:else}
                        <option value={schedule}>{schedule}</option>
                    {/if}
                {/each}
            </select>
            <div class="text-muted">Segure CTRL para selecionar mais de uma agenda</div>
            <div class="d-flex justify-content-center mt-4">
                <button class="btn btn-secondary me-1 fixed-width" on:click={abortImport}>Cancelar</button>
                <button class="btn btn-primary fixed-width ms-1" on:click={executeImport}>Importar</button>
            </div>
        </div>
    </Popover>
{/if}

<Content footer={true}>
    {#if step === 1}
        <Zact>
            <div class="spacer" />
            <button class="btn btn-secondary" on:click={importFile}><Fa icon={faFileImport} /> Importar</button>
            <button class="btn btn-primary" on:click={newEntity}><Fa icon={faPlusCircle} /> Nova agenda</button>
        </Zact>
        <Zgrid {grid} class="h-space" />
    {/if}
    {#if step === 4}
        <Zact>
            <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer" />
            {#if window.store.token.permissions.tools.scheduler.write}
                <button class="btn btn-primary" on:click={() => proccessImport(true)}><Fa icon={faSave} /> Importar amostra (10 primeiros)</button>
                <button class="btn btn-primary" on:click={() => proccessImport(false)}><Fa icon={faSave} /> Importar</button>
            {/if}
        </Zact>
        <Zframe class="h-space" title="Importação">
            <AudienceFileImport
                types={csvTypes}
                {patherns}
                bind:data={csvData}
                bind:valid={validImport}
                instructions="Selecione um arquivo XLSX ou CSV com os dados a serem importados."
            />
        </Zframe>
    {/if}
    {#if step === 2}
        <Zact>
            <button class="btn btn-secondary" on:click={showEntities}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer" />
            {#if !!entity[entityId]}
                <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
            {/if}
            <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
        </Zact>

        <Zframe title="Agenda" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput label="Nome da agenda" size={6} sizeM={12} type="text" required={true} bind:value={entity.label} />
                <Zinput label="Posição da agenda" size={6} sizeM={12} type="select" options={positionOptions} bind:value={entity.position} />
                <ZContainerCol size={6} sizeM={12}>
                    <ZentitySelect fonte="grupoAgenda" label="Grupo da agenda" opcaoVazia="Outras" bind:value={entity.group} />
                </ZContainerCol>
                <Zinput
                    label="Largura padrão"
                    size={6}
                    sizeM={12}
                    type="select"
                    options={widthFactorOptions}
                    bind:value={entity.widthFactor}
                    help="Largura da coluna desta agenda na tela da agenda, no computador. 2x ocupa o dobro do espaço. No celular não tem efeito."
                />
            </ZContainer>
        </Zframe>

        <Zframe title="Horários de atendimento" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput label="Hora de início" size={3} sizeM={6} type="time" required={true} bind:value={startTime} on:change={onStartChange} />
                <Zinput label="Hora de término" size={3} sizeM={6} type="time" required={true} bind:value={endTime} on:change={onStartChange} />
                <Zinput
                    label="Intervalo entre agendamentos (minutos)"
                    size={3}
                    sizeM={6}
                    type="number"
                    required={true}
                    bind:value={entity.range}
                    help="Mínimo de 10 minutos e máximo de 1440 minutos (24 horas)."
                />
            </ZContainer>
            <div class="mt-3">
                <div class="text-muted mb-1">Selecione os horários de atendimento</div>
                <ScheduleConfigSchedulersRange start={entity.startMinute} end={entity.endMinute} interval={entity.range} bind:slots={entity.slots} />
                <div class="schedule-range-label text-muted mt-2">
                    <div class="item">
                        <div class="open" />
                        <div>Horário disponível</div>
                    </div>
                    <div class="item">
                        <div class="close" />
                        <div>Horário de intervalo</div>
                    </div>
                </div>
                <div class="text-muted">Clique e arraste o mouse para marcar de azul os horários de atendimento e de vermelho os horários de intervalo.</div>
            </div>
        </Zframe>

        <Zframe title="Atendentes permitidos nesta agenda" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <ZinputSwitch label="Todos os usuários podem atender nesta agenda" size={12} bind:value={attendantsAll} />
                {#if !attendantsAll}
                    <Zinput
                        label="Atendentes"
                        size={6}
                        sizeM={12}
                        type="multiselect"
                        options={userOptions}
                        bind:value={entity.attendants}
                        help="Selecione os usuários que podem atender agendamentos nesta agenda."
                    />
                {/if}
            </ZContainer>
        </Zframe>

        <Zframe title="Agendamento online" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <ZinputSwitch label="Permitir agendamentos online pelo aplicativo ou API" size={12} bind:value={entity.public} />
                {#if entity.public}
                    <!-- presign={false}: a imagem aparece no agendamento online, para quem não tem login — no
                         bucket protegido (padrão do Zinput) a URL só abriria com token. É o mesmo destino que o
                         ImagePic com upload={true} usava. -->
                    <Zinput label="Imagem de perfil" size={4} sizeM={12} type="file" presign={false} options={imageAccept} bind:value={entity.picture} />
                    <Zinput
                        label="Atendente para agendamentos online"
                        help="Atribuir o agendamento desta agenda para este atendente."
                        size={4}
                        sizeM={12}
                        type="select"
                        required={true}
                        options={userOptions}
                        bind:value={entity.onlineAtendant}
                    />
                    <Zinput
                        label="Encaixes permitidos"
                        help="Quantos agendamentos online podem ser marcados num horário que já tem agendamento. Informe 0 para não permitir encaixes."
                        size={4}
                        sizeM={12}
                        type="number"
                        bind:value={entity.onlineFitsAllowed}
                    />
                {/if}
            </ZContainer>
        </Zframe>

        <Zframe title="Usuários com permissão de acesso" class="h-space">
            <ZContainer gapRow="8px" gapCol="8px">
                <ZinputSwitch label="Todos os usuários" size={12} bind:value={usersAll} />
                {#if !usersAll}
                    <Zinput label="Usuários" size={6} sizeM={12} type="multiselect" options={userOptions} bind:value={entity.users} />
                {/if}
            </ZContainer>
            {#if !usersAll}
                <div class="alert alert-warning text-center mt-3">
                    Atenção: qualquer super usuário poderá alterar estas configurações.<br />
                    Caso queira impedir isso, desative a opção "super usuário" no menu Conta &gt; Usuários.
                </div>
            {/if}
        </Zframe>
    {/if}
    {#if step === 3}
        <Zact>
            <button class="btn btn-secondary" on:click={() => (step = 2)}><Fa icon={faAngleLeft} /> Voltar</button>
            <div class="spacer" />
        </Zact>
        <Zframe title="Exclusão da Agenda '{entity.label}'" class="h-space">
            <p>Você está prestes a excluir uma agenda. Lembre-se:</p>
            <ul>
                <li>Ao excluir uma agenda ela fica indisponível e você não conseguirá reativá-la.</li>
                <li>Caso uma agenda seja excluída acidentalmente acione o suporte imediatamente.</li>
                <li>A exclusão de uma agenda não excluirá seus agendamentos. Caso haja agendamentos as automações continuarão atuando.</li>
                <li>Caso não queria que uma automação continue atuando nos agendamentos de uma agenda excluída, primeiro exclua cada um dos agendamentos.</li>
            </ul>

            <div class="form-check mb-2">
                <input bind:checked={allowRemove} class="form-check-input" type="checkbox" id="flexCheckDefault" />
                <label class="form-check-label" for="flexCheckDefault"> Estou ciente destas informações e desejo excluir esta agenda </label>
            </div>
            <div class="text-center">
                <button disabled={!allowRemove} class="btn btn-warning" on:click={removeEntityDo}>Excluir a agenda '{entity.label}'</button>
            </div>
        </Zframe>
    {/if}
</Content>

<style>
    .text-muted {
        font-size: 14px;
    }
    .schedule-range-label {
        display: flex;
        margin-bottom: 8px;
    }
    .schedule-range-label .item {
        display: flex;
        margin-right: 16px;
        align-items: center;
    }
    .schedule-range-label .item .open {
        border-radius: 4px;
        background-color: #1565c0;
        width: 16px;
        height: 16px;
        margin-right: 6px;
    }
    .schedule-range-label .item .close {
        border-radius: 4px;
        background-color: #e57373;
        width: 16px;
        height: 16px;
        margin-right: 6px;
    }
</style>
